import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  BUCKET_URL_KEY,
  REGEX_URL,
  OrderType,
  RoutePath,
} from "@app/constants";
import CampaignService from "@app/services/http/campaign.service";
import { CampaignNotification, Meta, TypeToggleStatus } from "@app/types";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import { RequestParams } from "@core/services/http/http.type";
import StorageService from "@core/services/storage";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Checkbox from "@app/components/checkbox";
import useForceUpdate from "@core/hooks/use-force-update.hook";

function IconColumn({ data }: { data: CampaignNotification }) {
  const hasIcon = Boolean(data.rightIcon);
  const s3Url = StorageService.getObject(BUCKET_URL_KEY);

  const icon = (data.rightIcon || "") as string;
  const src = icon ? (icon.match(REGEX_URL) ? icon : s3Url + icon) : "";

  return (
    <div className="flex items-center">
      {hasIcon ? (
        <img
          src={src}
          alt="icon"
          className="w-9 h-9 rounded-md object-cover border border-[#F1F3F9]"
        />
      ) : (
        <div className="w-9 h-9 rounded-md bg-[#F1F3F9] border border-[#F1F3F9]" />
      )}
    </div>
  );
}

function TimeSendColumn({ data }: { data: CampaignNotification }) {
  if (!data.timeSend) return <div>-</div>;

  return <div>{data.timeSend}</div>;
}

function RepeatDaysColumn({ data }: { data: CampaignNotification }) {
  if (
    !data.repeatDays ||
    !Array.isArray(data.repeatDays) ||
    data.repeatDays.length === 0
  ) {
    return <div>-</div>;
  }

  return <div>{data.repeatDays.join(", ")}</div>;
}

// leave placeholder; actual ActiveCheckbox will be inside component to access forceUpdate

function TestingIdsColumn({ data }: { data: CampaignNotification }) {
  const value = Array.isArray(data.testingIds)
    ? data.testingIds.join(", ")
    : data.testingIds || "-";

  return (
    <div className="truncate min-w-[120px] max-w-[240px]">{String(value)}</div>
  );
}

const baseColumns: ColumnConfig[] = [
  {
    label: "ID",
    dataKey: "messageKey",
    width: "12%",
    tdClassName: "truncate min-w-[80px] max-w-[160px]",
    hasOrder: true,
  },
  {
    label: "Tiêu đề",
    dataKey: "title",
    width: "20%",
    tdClassName: "truncate min-w-[150px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Mô tả",
    dataKey: "body",
    width: "28%",
    tdClassName: "truncate min-w-[180px] max-w-[320px]",
    hasOrder: true,
  },
  {
    label: "Icon",
    dataKey: "rightIcon",
    width: "10%",
    component: IconColumn,
  },
  {
    label: "Thời gian",
    dataKey: "timeSend",
    width: "15%",
    tdClassName: "truncate min-w-[160px] max-w-[220px]",
    hasOrder: true,
    component: TimeSendColumn,
  },
  {
    label: "Lặp lại",
    dataKey: "repeatDays",
    width: "15%",
    tdClassName: "truncate min-w-[120px] max-w-[200px]",
    hasOrder: true,
    component: RepeatDaysColumn,
  },
  {
    label: "Id test",
    dataKey: "testingIds",
    width: "20%",
    component: TestingIdsColumn,
    hasOrder: false,
  },
];

function NotificationTable() {
  const [notifications, setNotifications] = useState<CampaignNotification[]>(
    []
  );
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();
  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();
  const navigate = useNavigate();

  useEffect(() => {
    CampaignService.getNotificationList(
      requestParams,
      abortController.signal
    ).then((res) => {
      setNotifications(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [requestParams, update]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleEditClick = (notification: CampaignNotification) => {
    navigate(
      `/${RoutePath.CAMPAIGN_MANAGEMENT}/${RoutePath.UPDATE_CAMPAIGN}/${notification.messageKey}?type=notifications`
    );
  };

  const ActiveCheckbox = ({ data }: { data: CampaignNotification }) => {
    const handleToggle = (e: any) => {
      e.stopPropagation();

      CampaignService.toggleStatus(
        data.messageKey,
        TypeToggleStatus.NOTIFICATION
      ).then(() => {
        forceUpdate();
      });
    };

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={Boolean(data.isActive)}
          onChange={handleToggle}
          checkboxClassName="w-4 h-4 cursor-pointer"
        />
      </div>
    );
  };

  const columnConfig: ColumnConfig[] = [
    ...baseColumns.slice(0, 6),
    {
      label: "Trạng thái",
      dataKey: "isActive",
      width: "10%",
      component: ({ data }: { data: CampaignNotification }) => (
        <ActiveCheckbox data={data} />
      ),
      hasOrder: true,
    },
    baseColumns[6],
  ];

  return (
    <div className="mt-6 rounded-xl bg-white py-6 px-5">
      <TitleDescription
        title="Danh sách thông báo"
        onInputChange={onSearchChange}
      />
      <div className="mt-6">
        <Table
          columnConfig={columnConfig}
          dataTable={notifications}
          hasAction
          onEditClick={handleEditClick}
          meta={meta}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onRowClickOrder={onOrderChange}
          componentEmpty={
            <div className="text-center py-3 bg-[#f4f4f4]">
              <p className="text-[#666] text-xl">Không có dữ liệu</p>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default NotificationTable;
